import React, { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Box, Divider, Typography } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import requests, { ApiError } from '../../api/client';
import { useSession } from '../../Session';
import { useFeedback } from '../common/Feedback';
import { useTranslation } from '../../i18n';
import { colors, radius } from '../../theme/tokens';
import { formatPhoneDisplay } from '../../utils/phone';
import { ROUTES } from '../../utils/routes';
import { PlanCard } from './PlanCard';
import { PaymentMethodDialog, type PaymentChoice } from './PaymentMethodDialog';
import { PixDialog } from './PixDialog';
import { BoletoDialog } from './BoletoDialog';
import type { Plan, RechargeResponse, RechargeResult } from '../../types/api';

/**
 * Passo 5 do Fluxo B: escolher o plano e a forma de pagamento.
 *
 * Equivale a `resources/views/planos_lista.blade.php` + `listaPlanosView`.
 * Os planos e a linha já estão na sessão (gravados ao validar o OTP); esta
 * tela não refaz nenhuma consulta, só solicita a recarga quando o cliente
 * escolhe o método.
 */

/**
 * O middleware pode devolver os dados da recarga aninhados em `recarga` ou na
 * raiz da resposta. O legado normalizava com `response.dados || response` —
 * mesma ideia aqui, para não depender do formato.
 */
function normalizeRecharge(response: RechargeResponse): RechargeResult {
  return response.recarga ?? (response as RechargeResult);
}

export function PlanList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, titular } = useSession();
  const { show, close } = useFeedback();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [pixData, setPixData] = useState<RechargeResult | null>(null);
  const [boletoData, setBoletoData] = useState<RechargeResult | null>(null);

  const handleChoose = useCallback(
    async (method: PaymentChoice) => {
      if (!session || !selectedPlan) return;

      const plan = selectedPlan;
      setSelectedPlan(null);

      // Cartão continua dentro do site: a tela de checkout recebe plano e
      // linha pela querystring e revalida os dois contra a sessão.
      if (method === 'CARTAO') {
        const query = `?id_plano=${encodeURIComponent(String(plan.id))}&fone=${encodeURIComponent(
          session.linha
        )}`;
        navigate(`${ROUTES.card}${query}`);
        return;
      }

      // Sem a prova de OTP não há o que tentar: a API recusaria, e o cliente
      // ficaria olhando um erro genérico. Melhor mandar refazer a verificação.
      if (!session.linhaToken) {
        show({ kind: 'error', title: t.common.error, text: t.api.sessionExpired });
        return;
      }

      show({ kind: 'loading', title: t.payment.processingTitle, text: t.payment.processingBody });

      try {
        const response = await requests.recharge({
          metodo_pagamento: method,
          // O SMS de confirmação é disparado pelo ERP, não por esta tela.
          sms: 'N',
          numeros: [{ numero: session.linha, id_plano: plan.id }],
          // Prova de que a linha passou pelo OTP; quem valida é a API.
          linha_token: session.linhaToken,
        });

        const dados = normalizeRecharge(response);

        if (method === 'PIX') {
          if (dados.pix_copia_cola || dados.pix_QRCode) {
            close();
            setPixData(dados);
          } else {
            show({ kind: 'error', title: t.common.error, text: t.payment.errors.noPix });
          }
          return;
        }

        if (dados.boleto_codigo_barras) {
          close();
          setBoletoData(dados);
        } else {
          show({ kind: 'error', title: t.common.error, text: t.payment.errors.noBoleto });
        }
      } catch (error) {
        const apiError = error instanceof ApiError ? error : null;
        // `message === 'timeout'` é como o cliente HTTP sinaliza ECONNABORTED.
        const text =
          apiError?.message === 'timeout'
            ? t.payment.errors.timeout
            : apiError?.message || t.payment.errors.generic;
        show({ kind: 'error', title: t.common.error, text });
      }
    },
    [session, selectedPlan, navigate, show, close, t]
  );

  /**
   * Sem linha validada (ou sem planos) não há o que escolher: volta para a
   * home, como o redirect do `listaPlanosView`.
   */
  if (!session || session.planos.length === 0) {
    return <Navigate to={ROUTES.home} replace />;
  }

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, pt: 4, pb: 8, px: { xs: 2, sm: 3, lg: 4 } }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', maxWidth: 768, mx: 'auto', mb: 6 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.875rem', md: '2.25rem' },
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: colors.textPrimary,
              mb: 2,
            }}
          >
            {t.plans.heading}
          </Typography>

          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: 1.5,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: radius.full,
              px: 3,
              py: 1.5,
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 20, color: colors.textMuted }} />
              <Typography sx={{ fontWeight: 500, color: colors.textPrimary }}>
                {titular ?? t.plans.customerFallback}
              </Typography>
            </Box>

            <Divider
              aria-hidden
              orientation="vertical"
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' }, borderColor: colors.border }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartphoneIcon sx={{ fontSize: 20, color: colors.textMuted }} />
              <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                {formatPhoneDisplay(session.linha)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 4,
          }}
        >
          {session.planos.map((plan, index) => (
            <PlanCard key={plan.id} plan={plan} index={index} onSelect={setSelectedPlan} />
          ))}
        </Box>
      </Box>

      <PaymentMethodDialog
        open={Boolean(selectedPlan)}
        onClose={() => setSelectedPlan(null)}
        onChoose={handleChoose}
      />

      {/*
        PIX e boleto são montados sob demanda: desmontar ao fechar é o que
        encerra o polling do PIX (equivalente ao `willClose` do legado).
      */}
      {pixData ? <PixDialog data={pixData} onClose={() => setPixData(null)} /> : null}
      {boletoData ? (
        <BoletoDialog data={boletoData} onClose={() => setBoletoData(null)} />
      ) : null}
    </Box>
  );
}
